import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import type { DefaultRootState } from "react-redux";
import {
  getIsTerminatingWorkflowRun,
  getRunHistoryDetailsData,
  getRunHistoryDetailsLoadingState,
} from "ee/selectors/workflowRunHistorySelectors";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
} from "@appsmith/ads";
import {
  fetchWorkflowRunHistoryDetails,
  terminateWorkflowRun,
} from "ee/actions/workflowRunHistoryActions";
import { RunHistoryDetailsListItem } from "./RunHistoryDetailsListItem";
import styled from "styled-components";
import { APP_MODE } from "entities/App";
import { WorkflowExecutionStatus } from "ee/constants/WorkflowConstants";
import {
  createMessage,
  WORKFLOW_RUN_HISTORY_LOADING_TEXT,
  WORKFLOW_RUN_TERMINATE_CANCEL,
  WORKFLOW_RUN_TERMINATE_CONFIRM,
  WORKFLOW_RUN_TERMINATE_CTA,
  WORKFLOW_RUN_TERMINATE_MODAL_BODY,
  WORKFLOW_RUN_TERMINATE_MODAL_TITLE,
} from "ee/constants/messages";
import fastdom from "fastdom";
import { WorkflowApprovalResolutionForm } from "ee/PluginActionEditor/components/PostActionRun/WorkflowApprovalResolution";
import type { WorkflowRunDetailsActivities } from "ee/reducers/uiReducers/workflowHistoryPaneReducer";
import type { PostActionRunConfig } from "api/types";
import { PostRunActionNames } from "ee/PluginActionEditor/components/PostActionRun/types";

interface RunHistoryDetailsProps {
  workflowId: string;
  selectedRunId: string;
  workflowMode: APP_MODE;
}

const ActivitiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  gap: 4px;
  padding-bottom: 4px;
`;

const RunHistoryDetailsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ExecutionStatusContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 24px;
`;

const FOLLOW_LATEST_ACTIVITIES_THRESHOLD_PX = 24;

export function RunHistoryDetailsList({
  selectedRunId,
  workflowId,
  workflowMode,
}: RunHistoryDetailsProps) {
  const dispatch = useDispatch();
  const isLoading = useSelector(getRunHistoryDetailsLoadingState);
  const isTerminating = useSelector(getIsTerminatingWorkflowRun);
  const [isTerminateConfirmOpen, setIsTerminateConfirmOpen] = useState(false);
  const data = useSelector((state: DefaultRootState) =>
    getRunHistoryDetailsData(state, selectedRunId),
  );
  // Interval to poll for run details
  const interval = useRef(0);

  // Ref to ensure autoscroll
  const activitiesContainerRef = useRef<HTMLDivElement>(null);
  const isFollowingLatestActivitiesRef = useRef(true);
  const lastActivitiesScrollTopRef = useRef(0);

  const openTerminateConfirm = useCallback(() => {
    setIsTerminateConfirmOpen(true);
  }, []);

  const closeTerminateConfirm = useCallback(() => {
    setIsTerminateConfirmOpen(false);
  }, []);

  const confirmTerminate = useCallback(() => {
    dispatch(terminateWorkflowRun(workflowId, selectedRunId, workflowMode));
    setIsTerminateConfirmOpen(false);
  }, [dispatch, selectedRunId, workflowId, workflowMode]);

  const dispatchCallback = useCallback(() => {
    dispatch(
      fetchWorkflowRunHistoryDetails(workflowId, selectedRunId, workflowMode),
    );
  }, [dispatch, workflowId, selectedRunId, workflowMode]);

  // Extract activities and workflow status from redux data
  const { activities, workflowStatus } = useMemo(() => {
    if (!data)
      return {
        workflowStatus:
          WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_UNSPECIFIED,
        activities: [],
      };

    return {
      workflowStatus:
        data.workflowStatus !== undefined
          ? data.workflowStatus
          : WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_FAILED,
      activities: data ? data.activities : [],
    };
  }, [data]);

  // Poll for run details every 2 seconds after last fetch
  useEffect(
    function pollForRunDetails() {
      if (selectedRunId === "default" || isLoading) return;

      if (
        workflowStatus ===
        WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_RUNNING
      ) {
        !!interval.current && clearInterval(interval.current);
        interval.current = setInterval(() => {
          dispatchCallback();
        }, 2000);

        return () => clearInterval(interval.current);
      } else if (
        workflowStatus ===
        WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_UNSPECIFIED
      ) {
        dispatchCallback();
      }
    },
    [dispatchCallback, selectedRunId, workflowStatus, isLoading],
  );

  useEffect(
    function followLatestActivitiesOnRunChange() {
      isFollowingLatestActivitiesRef.current = true;
    },
    [selectedRunId],
  );

  const handleActivitiesContainerScroll = useCallback(() => {
    const container = activitiesContainerRef.current;

    if (!container) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Our own smooth scroll only moves down, so only an upward scroll detaches.
    if (distanceFromBottom <= FOLLOW_LATEST_ACTIVITIES_THRESHOLD_PX) {
      isFollowingLatestActivitiesRef.current = true;
    } else if (scrollTop < lastActivitiesScrollTopRef.current) {
      isFollowingLatestActivitiesRef.current = false;
    }

    lastActivitiesScrollTopRef.current = scrollTop;
  }, []);

  useEffect(
    function scrollToBottom() {
      if (!isFollowingLatestActivitiesRef.current) {
        return;
      }

      fastdom.mutate(() => {
        const container = activitiesContainerRef.current;

        // FastDom runs this on a later frame. Recheck so a scroll up in
        // that gap is not overwritten.
        if (!container || !isFollowingLatestActivitiesRef.current) {
          return;
        }

        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    },
    [activities],
  );

  if (selectedRunId === "default") {
    return null;
  }

  function renderApprovalForm() {
    const lastActivity: WorkflowRunDetailsActivities =
      activities[activities.length - 1];

    if (
      workflowMode !== APP_MODE.EDIT ||
      !Object.hasOwn(lastActivity, "result") ||
      !lastActivity.result
    ) {
      return null;
    }

    const result = lastActivity.result as {
      request: Record<string, string>;
      response: Record<string, unknown>;
      postRunAction?: PostActionRunConfig;
    };

    if (
      Object.hasOwn(result, "postRunAction") &&
      result.postRunAction?.name ===
        PostRunActionNames.WORKFLOW_APPROVAL_RESOLUTION
    ) {
      const response = result.response as Record<
        string,
        string | Array<string>
      >;
      const allowedResolutions = response.allowedResolutions as Array<string>;
      const requestId = response.id as string;

      if (
        allowedResolutions &&
        requestId &&
        Array.isArray(allowedResolutions) &&
        allowedResolutions.length > 0 &&
        requestId.length > 0
      ) {
        return (
          <WorkflowApprovalResolutionForm
            actionId={""}
            allowedResolutions={allowedResolutions}
            baseActionId={""}
            requestId={requestId}
            workflowId={workflowId}
          />
        );
      }
    }

    return null;
  }

  const isRunActive =
    workflowStatus ===
    WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_RUNNING;

  return (
    <RunHistoryDetailsListContainer data-testid="t--run-history-details-list-container">
      <ActivitiesContainer
        data-testid="t--run-history-details-list"
        onScroll={handleActivitiesContainerScroll}
        ref={activitiesContainerRef}
      >
        {activities.length > 0 ? (
          <>
            {activities.map((activity) => (
              <RunHistoryDetailsListItem
                data={activity}
                key={activity.activityId}
              />
            ))}
            {renderApprovalForm()}
          </>
        ) : null}
      </ActivitiesContainer>
      {isRunActive && (
        <ExecutionStatusContainer data-testid="t--run-history-details-exec-status">
          <Spinner className="mr-[8px]" size="md" />
          <Text>{createMessage(WORKFLOW_RUN_HISTORY_LOADING_TEXT)}</Text>
          <Button
            className="ml-auto mr-[8px]"
            data-testid="t--run-history-terminate"
            isLoading={isTerminating}
            kind="error"
            onClick={openTerminateConfirm}
            size="sm"
            startIcon="close-circle-line"
          >
            {createMessage(WORKFLOW_RUN_TERMINATE_CTA)}
          </Button>
          <Modal
            onOpenChange={setIsTerminateConfirmOpen}
            open={isTerminateConfirmOpen}
          >
            <ModalContent>
              <ModalHeader>
                {createMessage(WORKFLOW_RUN_TERMINATE_MODAL_TITLE)}
              </ModalHeader>
              <ModalBody>
                {createMessage(WORKFLOW_RUN_TERMINATE_MODAL_BODY)}
              </ModalBody>
              <ModalFooter>
                <Button
                  kind="secondary"
                  onClick={closeTerminateConfirm}
                  size="md"
                >
                  {createMessage(WORKFLOW_RUN_TERMINATE_CANCEL)}
                </Button>
                <Button
                  data-testid="t--run-history-terminate-confirm"
                  kind="error"
                  onClick={confirmTerminate}
                  size="md"
                >
                  {createMessage(WORKFLOW_RUN_TERMINATE_CONFIRM)}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </ExecutionStatusContainer>
      )}
    </RunHistoryDetailsListContainer>
  );
}
